import type { IBeneficiar, IRepresentative, TBeneficiarCustomField } from "containers/Beneficiars/types";
import { socialProfileList } from "containers/NewBeneficiaryContainer";
import { isObject } from "helpers/utils";
import { t } from "i18next";
import type { DAS2DeclarationLine } from "store/contributionStore";
import type { ICustomField } from "store/customFieldsStore";
import { BeneficiaryVAT, TBeneficiaryPrecount } from "store/types";

/**
 * Format beneficiary "draft" value data
 * @param value beneficiary key value
 */
export const getValueOrDraft = (value: string) => {
    const isDefaultDraft = value === "draft" || value?.startsWith("draft_");
    if (isDefaultDraft) return "";
    else return value;
};

/**
 * When sending beneficiary form / or title form, if the value was "draft", send "draft" instead of send "" value
 * @param defaultValue beneficiary default field value
 * @param newValue new field value
 */
export const shouldDraft = (defaultValue: string, newValue: string) => {
    if (defaultValue === "draft" && !newValue) return "draft"; // beneficiary form draft fields
    if (defaultValue?.startsWith("draft_") && !newValue) return defaultValue; // title form ean13 draft field
    else return newValue;
};

export const isInvitedAuthor = (values: string[]) => {
    return values.some((value) => value === "draft");
};

/**
 * Find "draft" values in Beneficiary fields
 * @param beneficiar Beneficiary data
 */
export const getDraftFields = (beneficiar: IBeneficiar) => {
    const draftFields: string[] = [];
    const expectedDraftFields = ["street", "siretNumber", "socialSecurityNumber", "zipCode", "city", "phoneNumber"];
    for (const key in beneficiar) {
        if (beneficiar[key] === "draft" && expectedDraftFields.includes(key)) draftFields.push(key);
    }
    return draftFields;
};

/**
 * Find mandatory custom fields + check if value is empty ("")
 */
export const getEmptyMandatoryCustomFields = (
    customFields: ICustomField[],
    customFieldsData: TBeneficiarCustomField[],
) => {
    const requiredCustomFields: ICustomField[] = customFields.filter((field) => !!field.isRequired);
    if (isObject(customFieldsData)) {
        const emptyFields: ICustomField[] = [];
        requiredCustomFields.forEach((field) => {
            if (!customFieldsData[field.uuid]?.length) emptyFields.push(field);
        });
        return emptyFields;
    } else {
        const emptyFields = requiredCustomFields?.filter(
            (field) =>
                !(customFieldsData as TBeneficiarCustomField[])?.find(
                    (fieldData) => fieldData.customFieldTypeId === field.id,
                )?.value,
        );
        return emptyFields;
    }
};

/**
 * Returns social profile as {type: value, description: text}
 * @param item: Data of person (beneficiary or representative)
 * @param distributorType: Type of distributor (see URSSAF list)
 */
export const getSocialProfile = (item: IBeneficiar | IRepresentative, distributorType: number | undefined) => {
    return item.socialProfileType
        ? socialProfileList[(distributorType ?? 0) - 1].find(
              (socialProfile) => socialProfile.type == item?.socialProfileType,
          )
        : null;
};

/**
 * Returns social profile description:
 * If company is Belgian: "Oui" | "Non"
 * If company is French: "1 - description"
 * @param item Data of beneficiary or representative
 * @param isCompanyBelgian Whether company is Belgian (and so data handled differently)
 * @param distributorType Type of distributor (see URSSAF list)
 */
export const formatSocialProfileType = ({
    item,
    isCompanyBelgian,
    distributorType,
}: {
    item: IRepresentative | IBeneficiar | DAS2DeclarationLine;
    isCompanyBelgian: boolean;
    distributorType?: number | undefined;
}) => {
    if (isCompanyBelgian) {
        return item.precount == TBeneficiaryPrecount.PRECOUNTED
            ? t("Beneficiars.PrecountYes")
            : t("Beneficiars.PrecountNo");
    } else if (distributorType) {
        return `${getSocialProfile(item as IBeneficiar, distributorType)?.type} - ${getSocialProfile(
            item as IBeneficiar,
            distributorType,
        )?.description}`;
    }
};

/**
 * Returns vat description as "Retenue à la source"
 * @param vatValue Vat type as saved in beneficiary/representative data.
 */
export const formatVatType = (vatValue: BeneficiaryVAT) => {
    switch (+vatValue) {
        case BeneficiaryVAT.FRANCHISE:
            return t("Beneficiars.VATExemption");
        case BeneficiaryVAT.VAT:
            return t("Beneficiars.VATYes");
        case BeneficiaryVAT.RETENUE_SOURCE:
            return t("Beneficiars.VATNo");
        case BeneficiaryVAT.TWENTY_PERCENT:
            return t("Beneficiars.VATTwentyPercent");
        case BeneficiaryVAT.TPS_ONLY:
            return t("Beneficiars.VAT_TPS_ONLY");
        case BeneficiaryVAT.TPS_TVQ:
            return t("Beneficiars.VAT_TPS_TVQ");
        default:
            return "Unknown VAT";
    }
};

export const getAccountingTypologyName = (typology: string): string => {
    if (typology === "Par défaut") {
        return t("Accounting.DefaultTypology");
    } else {
        return typology ?? t("Accounting.DefaultTypology");
    }
};

export const getCleanLanguageCode = (lang: string | undefined) => {
    const code = lang ?? "en";
    if (code.toLowerCase().includes("en")) return "en";
    if (code.toLowerCase().includes("fr")) return "fr";
    return "en"; // If their language matches none of our accepted languages, then default to english (they might be from other foreign country).
    //TODO: handle this more generically once we introduce more languages.
};

/**
 * Languages the backend accepts in per-language email defaults maps
 * (subjectByLang/bodyByLang). Must stay in sync with the backend's
 * `benefLangShort` normalization in `generator.controller.ts`.
 */
export const SUPPORTED_EMAIL_LANGS = ["fr", "en"] as const;
